import React, { Dispatch, SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import moment, { unitOfTime } from 'moment';
import { ChatRoom } from './types';

const currentDate = moment();

const timeSegments: [string, number][] = [
  ['seconds', 60],
  ['minutes', 60],
  ['hours', 24],
  ['days', 365],
];

type ChatListItemProps = ChatRoom & {
  onClick?: (e: any) => void;
};

const ChatListItem: React.FC<ChatListItemProps> = ({
  title,
  lastMessage,
  onClick,
}) => {
  const { t } = useTranslation();

  const [timeDiff, timeSegment] = useMemo(() => {
    if (lastMessage?.sentAt) {
      let diff: number = 0;
      let seg: string = '';
      const lastActivityMoment = moment.parseZone(lastMessage.sentAt);

      timeSegments.forEach(([segment], index) => {
        if (!diff || diff > timeSegments[index - 1][1]) {
          diff = Math.abs(
            currentDate.diff(lastActivityMoment, segment as unitOfTime.Base)
          );
          seg = segment;
        } else {
          return false;
        }
      });

      return [diff, seg];
    }

    return [undefined, undefined];
  }, [lastMessage]);

  return (
    <div className="cursor-pointer" onClick={onClick}>
      <div className="flex justify-between items-center pt-3">
        <h3 className="font-medium">#{title}</h3>
        {timeDiff && timeSegment && (
          <div className="text-grey-50 text-sm">{`${timeDiff} ${t(
            `chatroom.${timeSegment}`
          )}`}</div>
        )}
      </div>

      {lastMessage?.content && (
        <div className="flex justify-between">
          <p className="text-grey-50 text-sm">{lastMessage.content}</p>
        </div>
      )}
    </div>
  );
};

interface ChatListProps {
  setChatId: Dispatch<SetStateAction<string>>;
  chatRooms: ChatRoom[];
}

const ChatList: React.FC<ChatListProps> = ({
  setChatId,
  chatRooms: rawChatRooms,
}) => {
  const { t } = useTranslation();

  const chatRooms = rawChatRooms.filter(({ disabled }) => !disabled);

  const groupedChatRooms = [
    {
      title: t('chatroom.available'),
      items: chatRooms,
    },
  ];

  return (
    <>
      {groupedChatRooms.map(({ title, items: chatRooms }, index) => (
        <div className="px-4 pt-4 pb-4 overflow-y-auto" key={index}>
          <h2 className="uppercase text-grey-70 pb-4 text-sm dark:text-white font-medium">
            {title}
          </h2>
          {chatRooms.map((chatRoom) => (
            <ChatListItem
              key={chatRoom.id}
              {...chatRoom}
              onClick={() => setChatId(chatRoom.id)}
            />
          ))}
        </div>
      ))}
    </>
  );
};

export default ChatList;
